import type { FilterGroup } from '../constants';
import type { AnimalSearchFieldLookup, DynamicFieldOption } from './constants';
import {
  expandWildcardPattern,
  isIdentChipQuickSearchField,
} from './animalIdentChipFilter';
import { canNormalizeAiIdentifierFilter } from './normalizeAiSearchFilters';

export type AiSearchFieldDescriptor = {
  field: string;
  label: string;
  options?: { code: string; label: string }[];
};

export const AI_SEARCH_UNSUPPORTED_REASON =
  'That search uses a field or option that is not available for this society. Please choose from the available search filters.';

/** Use manual-search field options, omitting predicates and unmapped choices. */
export function buildAiSearchFieldContext(
  fields: readonly DynamicFieldOption[],
  formatLookupLabel: (lookup: AnimalSearchFieldLookup) => string,
): AiSearchFieldDescriptor[] {
  return fields.flatMap((field) => {
    if (field.value.startsWith('__lookup__')) {
      const options = (field.lookups ?? [])
        .filter((lookup) => lookup.i6LookupString?.trim())
        .map((lookup) => ({
          code: lookup.code,
          label: formatLookupLabel(lookup),
        }));
      return options.length
        ? [{ field: field.value, label: field.label, options }]
        : [];
    }
    return [{ field: field.value, label: field.label }];
  });
}

/** Metadata is untrusted client input. Keep only the descriptor contract. */
export function parseAiSearchFieldContext(
  value: unknown,
): AiSearchFieldDescriptor[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const field = item as Record<string, unknown>;
    if (typeof field.field !== 'string' || typeof field.label !== 'string')
      return [];
    if (field.field.startsWith('__lookup__')) {
      const options = Array.isArray(field.options)
        ? field.options.flatMap((option: unknown) => {
            if (!option || typeof option !== 'object') return [];
            const choice = option as Record<string, unknown>;
            return typeof choice.code === 'string' &&
              typeof choice.label === 'string'
              ? [{ code: choice.code, label: choice.label }]
              : [];
          })
        : [];
      return options.length
        ? [{ field: field.field, label: field.label, options }]
        : [];
    }
    return [{ field: field.field, label: field.label }];
  });
}

const SCALAR_OPERATORS = new Set([
  'STARTS_WITH',
  'CONTAINS',
  'ENDS_WITH',
  'EQUALS',
  'NOT_EQUALS',
  'GREATER_THAN',
  'LESS_THAN',
  'GREATER_THAN_OR_EQUAL_TO',
  'LESS_THAN_OR_EQUAL_TO',
  'NOT_NULL',
]);

type AiSearchFilters = {
  filterGroups: FilterGroup[];
  groupsOperator: string;
};

/** Reject the whole translation; dropping one invalid condition broadens it. */
export function hasValidAiSearchFieldContext(
  response: unknown,
  availableFields: readonly AiSearchFieldDescriptor[],
): response is AiSearchFilters {
  if (!response || typeof response !== 'object') return false;
  const data = response as Record<string, unknown>;
  if (data.groupsOperator !== 'AND' && data.groupsOperator !== 'OR')
    return false;
  if (!Array.isArray(data.filterGroups) || data.filterGroups.length === 0)
    return false;
  const fields = new Map(availableFields.map((field) => [field.field, field]));
  return data.filterGroups.every((group: unknown) => {
    if (!group || typeof group !== 'object') return false;
    const row = group as Record<string, unknown>;
    if (row.logicalOperator !== 'AND' && row.logicalOperator !== 'OR')
      return false;
    if (!Array.isArray(row.filters) || row.filters.length === 0) return false;
    return row.filters.every((filter: unknown) => {
      if (!filter || typeof filter !== 'object') return false;
      const item = filter as Record<string, unknown>;
      if (
        typeof item.field !== 'string' ||
        typeof item.operator !== 'string' ||
        typeof item.value !== 'string'
      )
        return false;
      const field = fields.get(item.field);
      if (!field) return false;
      if (field.options) {
        return (
          item.operator === 'EQUALS' &&
          field.options.some((option) => option.code === item.value)
        );
      }
      if (isIdentChipQuickSearchField(item.field)) {
        if (!canNormalizeAiIdentifierFilter(item.operator, item.value))
          return false;
        const chips = item.value
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);
        return (
          chips.length > 0 &&
          chips.every((chip) => expandWildcardPattern(chip).length > 0)
        );
      }
      return (
        SCALAR_OPERATORS.has(item.operator) &&
        (item.operator === 'NOT_NULL' || !!item.value.trim())
      );
    });
  });
}
